import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { practiceProfileApi, practiceUsersApi } from "apis";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { FirebaseStorageService } from "helpers/firebaseStorage";
import { PracticeProfileUpdateDto } from "libs/cellera-sdk";
import { UserProfileFormData } from "validations/userSchemas";

/**
 * API Query Keys for profile-related queries
 */
export const profileApiKeys = {
  all: ["profileApi"] as const,
  profile: () => [...profileApiKeys.all, "profile"] as const,
} as const;

export const useGetOwnProfileQuery = () => {
  return useQuery({
    queryKey: profileApiKeys.profile(),
    queryFn: async () => await practiceProfileApi.getPracticeProfile(),
    retry: 2,
  });
};

export const useUpdateOwnProfileMutation = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (profileData: PracticeProfileUpdateDto) => {
      if (!user?.uid) {
        throw new Error("User not authenticated");
      }
      return await practiceUsersApi.updatePracticeUser({
        userId: user.uid,
        practiceProfileUpdateDto: {
          firstName: profileData.firstName,
          middleName: profileData.middleName,
          lastName: profileData.lastName,
          npi: profileData.npi,
          phoneNumber: profileData.phoneNumber,
          profileImage: profileData.profileImage,
          specialty: profileData.specialty,
        },
      });
    },

    onSuccess: () => {
      // Invalidate and refetch profile data (including branches)
      queryClient.invalidateQueries({ queryKey: profileApiKeys.profile() });

      showSuccess(
        "Profile Updated",
        "Your profile has been updated successfully."
      );
    },

    onError: (error) => {
      console.error("API Error updating profile:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update profile. Please try again.";

      showError("Update Failed", errorMessage);
    },
  });
};

export const useProfileForm = () => {
  const {
    data: profileData,
    isLoading,
    isError,
    error,
  } = useGetOwnProfileQuery();
  const updateProfileMutation = useUpdateOwnProfileMutation();

  const submitProfile = async (
    data: UserProfileFormData,
    currentImageUrl: string | null
  ) => {
    let profileImageUrl = currentImageUrl || "";

    // Handle profile image upload if it's a File object
    if (data.profileImage instanceof File) {
      try {
        profileImageUrl = await FirebaseStorageService.uploadProfileImage(
          data.profileImage
        );
      } catch {
        throw new Error("Failed to upload profile image. Please try again.");
      }
    } else if (typeof data.profileImage === "string") {
      // If it's already a string URL, use it
      profileImageUrl = data.profileImage;
    }

    return updateProfileMutation.mutateAsync({
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      npi: data.npi ?? "",
      phoneNumber: data.phoneNumber ?? "",
      profileImage: profileImageUrl,
      specialty: data.specialties.filter(
        (specialty) => specialty !== undefined
      ),
    });
  };

  return {
    profileData,
    isLoading: isLoading || updateProfileMutation.isPending,
    isSubmitting: updateProfileMutation.isPending,
    isError: isError || updateProfileMutation.isError,
    error: error || updateProfileMutation.error,
    submitProfile,
    reset: () => updateProfileMutation.reset(),
  };
};
